import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Trophy,
  Target,
  TrendingUp,
  TrendingDown,
  Minus,
  Loader2,
  ArrowLeft,
  Crown,
  Medal,
  Award,
  Star,
  Play,
  Flame,
  BarChart3,
  Users
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SKILL_LABELS = {
  discovery_quality: 'Discovery',
  objection_handling: 'Objections',
  question_quality: 'Questions',
  conversation_control: 'Control',
  closing_skill: 'Closing'
};

const ACHIEVEMENT_ICONS = {
  play: Play,
  target: Target,
  flame: Flame,
  trophy: Trophy,
  star: Star,
  award: Award,
  crown: Crown,
  'trending-up': TrendingUp
};

function RankBadge({ rank }) {
  if (rank === 1) return <div className="w-8 h-8 rounded-full bg-yellow-500/20 flex items-center justify-center"><Crown className="w-4 h-4 text-amber-600" /></div>;
  if (rank === 2) return <div className="w-8 h-8 rounded-full bg-slate-400/20 flex items-center justify-center"><Medal className="w-4 h-4 text-slate-300" /></div>;
  if (rank === 3) return <div className="w-8 h-8 rounded-full bg-orange-600/20 flex items-center justify-center"><Medal className="w-4 h-4 text-orange-600" /></div>;
  return <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center"><span className="text-xs font-bold text-muted-foreground">{rank}</span></div>;
}

export default function Leaderboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('all');

  useEffect(() => {
    fetchLeaderboard();
  }, [period]);

  const fetchLeaderboard = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/roleplay/leaderboard?period=${period}`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-600';
    if (score >= 60) return 'text-amber-600';
    if (score >= 40) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreBarColor = (score) => {
    if (score >= 80) return 'bg-emerald-500';
    if (score >= 60) return 'bg-yellow-500';
    if (score >= 40) return 'bg-orange-500';
    return 'bg-red-500';
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="leaderboard-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate('/practice')}
              className="mb-2 -ml-2 text-muted-foreground hover:text-foreground"
              data-testid="back-to-practice"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Practice
            </Button>
            <h1 className="font-heading text-2xl font-bold text-foreground flex items-center gap-3">
              <Trophy className="w-7 h-7 text-amber-600" />
              Team Leaderboard
            </h1>
            <p className="text-muted-foreground mt-1">
              {data?.total_participants || 0} participants
            </p>
          </div>

          {/* Period filter */}
          <div className="flex gap-1 bg-card border border-border rounded-lg p-1">
            {[
              { id: 'week', label: 'Week' },
              { id: 'month', label: 'Month' },
              { id: 'all', label: 'All Time' }
            ].map(p => (
              <button
                key={p.id}
                onClick={() => setPeriod(p.id)}
                data-testid={`period-${p.id}`}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  period === p.id
                    ? 'bg-primary/15 text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : data?.leaderboard?.length > 0 ? (
          <div className="space-y-4">
            {/* Top 3 Podium */}
            {data.leaderboard.length >= 3 && (
              <div className="grid grid-cols-3 gap-3 mb-2">
                {/* 2nd place */}
                <Card className="bg-card border-slate-400/20 mt-8">
                  <CardContent className="p-4 text-center">
                    <RankBadge rank={2} />
                    <div className="mx-auto mt-2">
                      <p className="font-medium text-sm text-foreground truncate">{data.leaderboard[1]?.name}</p>
                      <p className={`font-heading text-2xl font-bold ${getScoreColor(data.leaderboard[1]?.average_score)}`}>
                        {data.leaderboard[1]?.average_score}
                      </p>
                      <p className="text-xs text-muted-foreground">{data.leaderboard[1]?.total_sessions} sessions</p>
                    </div>
                  </CardContent>
                </Card>
                {/* 1st place */}
                <Card className="bg-card border-yellow-500/30 shadow-lg shadow-yellow-500/5">
                  <CardContent className="p-4 text-center">
                    <div className="mx-auto">
                      <RankBadge rank={1} />
                    </div>
                    <div className="mt-2">
                      <p className="font-medium text-sm text-foreground truncate">{data.leaderboard[0]?.name}</p>
                      <p className={`font-heading text-3xl font-bold ${getScoreColor(data.leaderboard[0]?.average_score)}`}>
                        {data.leaderboard[0]?.average_score}
                      </p>
                      <p className="text-xs text-muted-foreground">{data.leaderboard[0]?.total_sessions} sessions</p>
                    </div>
                  </CardContent>
                </Card>
                {/* 3rd place */}
                <Card className="bg-card border-orange-500/20 mt-12">
                  <CardContent className="p-4 text-center">
                    <RankBadge rank={3} />
                    <div className="mx-auto mt-2">
                      <p className="font-medium text-sm text-foreground truncate">{data.leaderboard[2]?.name}</p>
                      <p className={`font-heading text-2xl font-bold ${getScoreColor(data.leaderboard[2]?.average_score)}`}>
                        {data.leaderboard[2]?.average_score}
                      </p>
                      <p className="text-xs text-muted-foreground">{data.leaderboard[2]?.total_sessions} sessions</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Full Rankings */}
            <Card className="bg-card border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Full Rankings
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {data.leaderboard.map(entry => (
                    <div
                      key={entry.user_id}
                      data-testid={`lb-entry-${entry.user_id}`}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${
                        entry.is_current_user
                          ? 'bg-primary/5 border-primary/30 ring-1 ring-primary/20'
                          : 'bg-secondary/20 border-border/40'
                      }`}
                    >
                      <RankBadge rank={entry.rank} />

                      {/* Name + Role */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground truncate">
                            {entry.name}
                            {entry.is_current_user && <span className="text-xs text-primary ml-1">(you)</span>}
                          </span>
                          {entry.role === 'manager' && (
                            <Badge className="bg-primary/10 text-primary border-primary/20 border text-[10px]">mgr</Badge>
                          )}
                        </div>
                        {/* Achievements */}
                        <div className="flex gap-1 mt-1">
                          {entry.achievements?.slice(0, 5).map(achId => {
                            const ach = data.achievements_list?.find(a => a.id === achId);
                            if (!ach) return null;
                            const AchIcon = ACHIEVEMENT_ICONS[ach.icon] || Star;
                            return (
                              <div key={achId} title={ach.name} className="w-5 h-5 rounded-full bg-yellow-500/15 flex items-center justify-center">
                                <AchIcon className="w-3 h-3 text-amber-600" />
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <div className="text-center">
                          <p className="font-mono text-foreground">{entry.total_sessions}</p>
                          <p>sessions</p>
                        </div>
                        <div className="text-center">
                          <p className={`font-mono font-bold ${getScoreColor(entry.best_score)}`}>{entry.best_score}</p>
                          <p>best</p>
                        </div>
                      </div>

                      {/* Average Score */}
                      <div className="text-center w-16">
                        <p className={`font-heading text-xl font-bold ${getScoreColor(entry.average_score)}`}>
                          {entry.average_score}
                        </p>
                        <p className="text-[10px] text-muted-foreground">avg</p>
                      </div>

                      {/* Trend */}
                      <div className="flex items-center gap-1 w-12">
                        {entry.trend > 0 ? (
                          <TrendingUp className="w-4 h-4 text-emerald-600" />
                        ) : entry.trend < 0 ? (
                          <TrendingDown className="w-4 h-4 text-red-600" />
                        ) : (
                          <Minus className="w-4 h-4 text-muted-foreground" />
                        )}
                        <span className={`text-xs font-mono ${
                          entry.trend > 0 ? 'text-emerald-600' : entry.trend < 0 ? 'text-red-600' : 'text-muted-foreground'
                        }`}>
                          {entry.trend > 0 ? '+' : ''}{entry.trend}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Skill Comparison (expanded view for first entry or current user) */}
            {data.leaderboard.length > 0 && (
              <Card className="bg-card border-border/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    Skill Comparison
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(SKILL_LABELS).map(([key, label]) => (
                      <div key={key}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-muted-foreground">{label}</span>
                        </div>
                        <div className="space-y-1">
                          {data.leaderboard.slice(0, 5).map(entry => {
                            const val = entry.skill_averages?.[key] || 0;
                            return (
                              <div key={entry.user_id} className="flex items-center gap-2">
                                <span className={`text-[10px] w-16 truncate ${entry.is_current_user ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                                  {entry.name.split(' ')[0]}
                                </span>
                                <div className="flex-1 h-2 rounded-full bg-secondary overflow-hidden">
                                  <div
                                    className={`h-full rounded-full transition-all ${entry.is_current_user ? 'bg-primary' : getScoreBarColor(val)}`}
                                    style={{ width: `${val}%` }}
                                  />
                                </div>
                                <span className={`text-xs font-mono w-6 text-right ${entry.is_current_user ? 'text-primary' : 'text-foreground'}`}>{val}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          <div className="text-center py-16 text-muted-foreground">
            <Trophy className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p>No leaderboard data yet</p>
            <p className="text-sm mt-1">Complete roleplay sessions to appear on the leaderboard</p>
            <Button onClick={() => navigate('/practice')} className="mt-4">
              Start Practicing
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
}
